/* eslint-disable react-hooks/exhaustive-deps */
/*
 * File: AssignedRule.tsx
 * Project: beaker
 * -----
 * Last Modified: 27 Apr 2022 11:11:11 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, Menu, message, Row } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { CreateUserForm } from 'components/molecules/CreateUserForm';
import { Stack } from 'components/UI';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { FormikProvider, useFormik } from 'formik';
import { createUserPayload, CreateUserPayload } from 'models/Users';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCreateUserMutation, useGetUsersQuery } from 'redux/services/cookieMonster/cookieMonsterApi';
import { resetSelectedDivisions } from 'redux/slices/selectedDivisionSlice';
import { resetRoles } from 'redux/slices/selectedRoles';
import { ReduxState } from 'redux/store';

export const CreateUserPage: React.FC = () => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createUser] = useCreateUserMutation();
  const { userParams } = useSelector((state: ReduxState) => state);
  const { selectedRoles } = useSelector((state: ReduxState) => state.selectedRoles);
  const { selectedDivisions } = useSelector((state: ReduxState) => state.selectedDivisions);

  /* ******************** API Hooks ******************** */
  const { data: userData, isFetching: iseUserDataFetching } = useGetUsersQuery(userParams);

  const formik = useFormik<CreateUserPayload>({
    enableReinitialize: true,
    validationSchema: createUserPayload,
    initialValues: {
      isMdsiEmployee: false,
      email: '',
      givenName: '',
      familyName: '',
      employeeERPId: '',
      password: 'secreTpassword<>',
      divisionIdsToAssign: [],
      roleIdsToAssign: []
    },
    onSubmit: async (values) => {
      if (!userData) {
        message.error("User doesn't exist");

        return;
      }
      if (formik.values.divisionIdsToAssign !== []) {
        try {
          const updateData = await createUser({ payload: { ...values, employeeERPId: values.employeeERPId !== '' ? values.employeeERPId?.split('-')[1].trimStart() : '' } }).unwrap();
          navigate(`/users/${updateData.user_id}`);
          message.success(`User ${updateData.name} was successfully created!`);
        } catch (error) {
          console.log(error);
          message.error((error as { data: string }).data ?? 'User failed to be created.');
        }
      }
    }
  });
  /* ******************** Functions ******************** */

  const handleClose = (): void => {
    navigate('/');
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    await formik.submitForm();
    if (close) {
      handleClose();
    }
  };

  useEffect(() => {
    formik.setValues((prevState: any) => ({
      ...prevState,
      divisionIdsToAssign: selectedDivisions,
      roleIdsToAssign: selectedRoles
    }));
  }, [selectedDivisions, selectedRoles]);

  useEffect(() => {
    return (): void => {
      dispatch(resetSelectedDivisions());
      dispatch(resetRoles());
      formik.resetForm();
    };
  }, []);

  if (iseUserDataFetching) {
    return (
      <Row justify="center" align="middle">
        <Col span={24}>
          <BlockLoader direction="loader loader--slideUp" />
        </Col>
      </Row>
    );
  }
  const menuJSX = (
    <Menu disabledOverflow={true}>
      <Menu.Item key="1" onClick={(): Promise<void> => handleSave(false)}>
        Save
      </Menu.Item>
    </Menu>
  );

  const validUserDetailsStep = Boolean(formik.values.givenName && formik.values.familyName && formik.values.email);

  const validAssignRolesStep = Boolean(formik.values.roleIdsToAssign.length);
  const validDeliveryAddressStep = Boolean(formik.values.divisionIdsToAssign.length);
  const isValidPost = validUserDetailsStep && validAssignRolesStep && validDeliveryAddressStep && validDeliveryAddressStep;

  return (
    <Stack flexDirection="column">
      <FormikProvider value={formik}>
        <Col style={{ padding: 10, background: 'rgb(206, 213, 242)' }}>
          <Stack justifyContent="space-between" alignItems="center">
            <AntPageTitle text={`Create User Dashboard`} />
            <Stack>
              <Button onClick={handleClose} icon={<ArrowLeftOutlined />}>
                Back
              </Button>
              <Dropdown.Button overlay={menuJSX} onClick={(): Promise<void> => handleSave(true)} loading={formik.isSubmitting} type="primary" icon={<SaveOutlined />} disabled={!isValidPost}>
                Save and Exit
              </Dropdown.Button>
            </Stack>
          </Stack>
        </Col>

        <CreateUserForm />
      </FormikProvider>
    </Stack>
  );
};
